import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, SearchIcon, PlusIcon, ChevronRightIcon, BuildingIcon, SmartphoneIcon, ShieldIcon, StoreIcon, UserIcon, Edit3Icon, WalletIcon, CheckIcon, XIcon, ShareIcon, CalendarIcon, CopyIcon, ArrowRightIcon, RefreshCwIcon, BellIcon, ChevronDownIcon, CheckCircleIcon, CheckCircle2Icon, InfoIcon, FingerprintIcon, ScanFaceIcon, HomeIcon, ContactIcon, Image as ImageIcon, StarIcon, PencilIcon, Trash2Icon, AlertTriangleIcon, CreditCardIcon } from 'lucide-react';
import { NotificationPanel } from './NotificationPanel';
import { useTheme } from '../utils/theme';
interface SendMoneyScreenProps {
  onBack: () => void;
  onNavigateHome?: () => void;
}
type Step = 'select_recipient' | 'select_bank' | 'bank_Payees' | 'add_Payee' | 'verify_Payee' | 'Payee_saved' | 'enter_amount' | 'review' | 'mpin' | 'processing' | 'success';
interface Recipient {
  id: string;
  name: string;
  bank: string;
  accountNumber: string;
  icon: React.ElementType;
  color: string;
  nickname?: string;
  isFavorite?: boolean;
}
const ALL_Payees: Recipient[] = [{
  id: '1',
  name: 'Ahmed Khan',
  bank: 'Bank of Punjab',
  accountNumber: '1234567890123456',
  icon: BuildingIcon,
  color: '#F37024',
  nickname: 'Brother Ahmed ',
  isFavorite: false
}, {
  id: '2',
  name: 'Sara Ali',
  bank: 'Bank of Punjab',
  accountNumber: '9876543210987654',
  icon: BuildingIcon,
  color: '#F37024',
  nickname: 'Sister Sara',
  isFavorite: false
}, {
  id: '3',
  name: 'Bilal Ahmed',
  bank: 'Jazz Cash',
  accountNumber: '03001234567',
  icon: SmartphoneIcon,
  color: '#F37024',
  nickname: 'Billu Friend',
  isFavorite: false
}, {
  id: '4',
  name: 'Fatima Zahra',
  bank: 'Easypaisa',
  accountNumber: '03211234567',
  icon: WalletIcon,
  color: '#F37024',
  nickname: 'clothing Business',
  isFavorite: false
}, {
  id: '5',
  name: 'Al-Fatah Supermarket',
  bank: 'RAAST',
  accountNumber: '03009998887',
  icon: StoreIcon,
  color: '#F37024',
  nickname: 'Groceries',
  isFavorite: false
}];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
const BANKS = ['Bank of Punjab', 'HBL', 'Meezan Bank', 'Bank Alfalah', 'Faysal Bank', 'JS Bank', 'UBL', 'MCB', 'Allied Bank', 'Standard Chartered', 'Jazz Cash', 'Easypaisa'];
interface PastPayment {
  amount: number;
  date: string;
  status: 'Success' | 'Failed';
}
const PAST_PAYMENTS: Record<string, PastPayment[]> = {
  '1': [{
    amount: 25000,
    date: '12 May 2026',
    status: 'Success'
  }, {
    amount: 10000,
    date: '28 Apr 2026',
    status: 'Success'
  }, {
    amount: 5000,
    date: '15 Apr 2026',
    status: 'Failed'
  }],
  '2': [{
    amount: 15000,
    date: '08 May 2026',
    status: 'Success'
  }, {
    amount: 7500,
    date: '22 Apr 2026',
    status: 'Success'
  }],
  '3': [{
    amount: 3000,
    date: '02 May 2026',
    status: 'Success'
  }],
  '4': []
};
let biometricCounter = 0;
// ── Shared CTA button style ────────────────────────────────────────────────
// height: 55px | width: 280px | border-radius: 8px | color: #F37024
const ctaStyle: React.CSSProperties = {
  height: '55px',
  width: '280px',
  borderRadius: '8px',
  background: '#F37024',
  color: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '16px',
  border: 'none',
  cursor: 'pointer',
  transition: 'opacity 0.15s',
  margin: '0 auto'
};
export function SendMoneyScreen({
  onBack,
  onNavigateHome
}: SendMoneyScreenProps) {
  const {
    isDark
  } = useTheme();
  const [step, setStep] = useState<Step>('select_recipient');
  const [direction, setDirection] = useState(1);
  const [showNotifications, setShowNotifications] = useState(false);
  const [Payees, setPayees] = useState<Recipient[]>(ALL_Payees);
  const [editingPayeeId, setEditingPayeeId] = useState<string | null>(null);
  const [editingNickname, setEditingNickname] = useState('');
  const [deleteCandidate, setDeleteCandidate] = useState<Recipient | null>(null);
  const [PayeeToast, setPayeeToast] = useState<string | null>(null);
  const [expandedPayee, setExpandedPayee] = useState<string | null>(null);
  const confirmDeletePayee = () => {
    if (!deleteCandidate) return;
    setPayees(prev => prev.filter(b => b.id !== deleteCandidate.id));
    setDeleteCandidate(null);
    setPayeeToast('Payee removed successfully');
  };
  useEffect(() => {
    if (PayeeToast) {
      const t = setTimeout(() => setPayeeToast(null), 2500);
      return () => clearTimeout(t);
    }
  }, [PayeeToast]);
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [selectedQuickBank, setSelectedQuickBank] = useState<string>('');
  const [bankPayeesSearch, setBankPayeesSearch] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [purpose, setPurpose] = useState('Other');
  const [showPurposeDropdown, setShowPurposeDropdown] = useState(false);
  const [mpin, setMpin] = useState('');
  const [biometricPhase, setBiometricPhase] = useState<'scanning' | 'verified' | 'entering_mpin'>('scanning');
  const [scanType, setScanType] = useState<'face' | 'thumb'>('face');
  const PURPOSES = ['Family Support', 'Bill Payment', 'Salary', 'Business Payment', 'Loan Repayment', 'Gift', 'Other'];
  const [newPayeeName, setNewPayeeName] = useState('');
  const [newPayeeAccount, setNewPayeeAccount] = useState('');
  const [accountInputType, setAccountInputType] = useState<'account' | 'iban'>('account');
  const [newPayeeRelationship, setNewPayeeRelationship] = useState('');
  const [newPayeeBank, setNewPayeeBank] = useState('');
  const [newPayeeNickname, setNewPayeeNickname] = useState('');
  const [bankSearch, setBankSearch] = useState('');
  const [addPayeePhase, setAddPayeePhase] = useState<'entry' | 'fetching' | 'details_fetched' | 'preparing_payment'>('entry');
  const [savePayee, setSavePayee] = useState(false);
  const [nicknameError, setNicknameError] = useState('');
  const [cameFromQuickLink, setCameFromQuickLink] = useState(false);
  const [cameFromSavedPayee, setCameFromSavedPayee] = useState(false);
  const [isOwnAccountTransfer, setIsOwnAccountTransfer] = useState(false);
  const [showBopInfo, setShowBopInfo] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [transferMethod, setTransferMethod] = useState<'raast' | '1link'>('raast');
  const [paymentTab, setPaymentTab] = useState<'raast' | 'other'>('raast');
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(true);
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const [selectedBankForPayment, setSelectedBankForPayment] = useState<string>('');
  const PAY_FROM_ACCOUNTS = [{
    id: 'current',
    holder: 'Kashish Shewani',
    label: 'Current Account',
    number: '......0123 4567 8910',
    balance: 782354,
    isCard: false
  }, {
    id: 'savings',
    holder: 'Kashish Shewani',
    label: 'Savings Account',
    number: '......0456 7890 1234',
    balance: 145200,
    isCard: false
  }, {
    id: 'credit',
    holder: 'Kashish Shewani',
    label: 'Credit Card',
    number: '1234 5678 1234',
    balance: 0,
    isCard: true
  }];
  const [payFromAccount, setPayFromAccount] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showPayFromDropdown, setShowPayFromDropdown] = useState(false);
  const OWN_ACCOUNTS = PAY_FROM_ACCOUNTS.filter(a => !a.isCard);
  const [ownReceivingAccount, setOwnReceivingAccount] = useState(OWN_ACCOUNTS[1]);
  const [showOwnReceivingDropdown, setShowOwnReceivingDropdown] = useState(false);
  const sameAccountError = isOwnAccountTransfer && payFromAccount.id === ownReceivingAccount.id;
  useEffect(() => {
    if (payFromAccount.isCard && (paymentTab === 'raast' || selectedBankForPayment === 'RAAST')) {
      setPayFromAccount(PAY_FROM_ACCOUNTS[0]);
    }
  }, [paymentTab, selectedBankForPayment, payFromAccount.isCard]);
  const renderBankLogo = (bank: string, size: 'sm' | 'md' = 'md') => {
    const cls = size === 'sm' ? 'w-6 h-6' : 'w-full h-full';
    if (bank === 'Bank of Punjab' || bank === 'The Bank of Punjab') {
      return <img src="/Dark_theme_logo-removebg-preview.png" alt="BOP" className={`${cls} object-contain`} data-id="element-3085" data-mp="cw2h1w1ic26p" />;
    } else if (bank === 'HBL') {
      return <div className={`${cls} rounded-full bg-[#008B8B] flex items-center justify-center`} data-id="element-3086" data-mp="ltmjv9f0q6jf">
          <span className="text-white font-bold text-[10px]" data-id="element-3087" data-mp="qs65f8pp9gb9">HBL</span>
        </div>;
    } else if (bank === 'Meezan Bank') {
      return <svg viewBox="0 0 44 44" className={cls} data-id="element-3088" data-mp="10rn95kle5k4l">
          <circle cx="22" cy="22" r="20" fill="#7C3AED" stroke="#22C55E" strokeWidth="2" data-id="element-3089" data-mp="10qc2nd0k9y9s" />

          <path d="M22 12L26 20H18L22 12Z M18 24H26L22 32L18 24Z" fill="white" data-id="element-3090" data-mp="19k8osrtq7ap7" />
        </svg>;
    } else if (bank === 'Bank Alfalah') {
      return <svg viewBox="0 0 44 44" className={cls} data-id="element-3091" data-mp="1jjpuh98z9ol6">
          <circle cx="22" cy="22" r="20" fill="white" data-id="element-3092" data-mp="386kn8g0c2w5m" />
          <path d="M22 10L28 22L22 18L16 22L22 10Z" fill="#DC2626" data-id="element-3093" data-mp="3885ffyz6e30b" />
        </svg>;
    } else if (bank === 'Faysal Bank') {
      return <div className={`${cls} rounded-full bg-[#FBBF24] flex items-center justify-center`} data-id="element-3094" data-mp="3h7ir8yarv3o7">
          <span className="text-[#1A1A1A] font-bold text-[8px]" data-id="element-3095" data-mp="3lyiok4nsznr8">FAYSAL</span>
        </div>;
    } else if (bank === 'JS Bank') {
      return <svg viewBox="0 0 44 44" className={cls} data-id="element-3096" data-mp="3qwtzkipu44b1">
          <circle cx="22" cy="22" r="20" fill="white" data-id="element-3097" data-mp="3vvn960203b5x" />
          <text x="22" y="26" textAnchor="middle" className="text-[12px] font-bold" fill="#0EA5E9" data-id="element-3098" data-mp="3vy9h40sszsoo">
            JS
          </text>
        </svg>;
    } else if (bank === 'Jazz Cash') {
      return <svg viewBox="0 0 40 40" className={cls} fill="none" data-id="element-3099" data-mp="26itbuwi02r42">
          <path d="M8 20C8 14 12 8 20 8C24 8 27 10 29 13L25 17C23.5 15 21.8 14 20 14C15.6 14 13 17 13 20C13 23 15.6 26 20 26C21.8 26 23.5 25 25 23L29 27C27 30 24 32 20 32C12 32 8 26 8 20Z" fill="#DC2626" data-id="element-3100" data-mp="2aiak1xota2cx" />

          <path d="M28 12C30 14 32 17 32 20C32 23 30 26 28 28C26 26 24 23 24 20C24 17 26 14 28 12Z" fill="#FBBF24" data-id="element-3101" data-mp="1y79qqcrnmw6p" />
        </svg>;
    } else if (bank === 'Easypaisa') {
      return <svg viewBox="0 0 40 40" className={cls} fill="none" data-id="element-3102" data-mp="1p6373utpl598">
          <circle cx="20" cy="20" r="14" stroke="#22C55E" strokeWidth="3" fill="none" data-id="element-3103" data-mp="2lxn8nagizenk" />

          <path d="M12 20C12 15.6 15.6 12 20 12" stroke="#1A1A1A" strokeWidth="3" strokeLinecap="round" data-id="element-3104" data-mp="2cwpgbpfg8vkt" />
        </svg>;
    } else {
      const colors = ['#0EA5E9', '#22C55E', '#F37024', '#7C3AED', '#EF4444'];
      const colorIndex = bank.charCodeAt(0) % colors.length;
      return <div className={`${cls} rounded-full flex items-center justify-center`} style={{
        backgroundColor: colors[colorIndex]
      }} data-id="element-3105" data-mp="wlbqakqyvxiy">
          <span className="text-white font-bold text-[10px]" data-id="element-3106" data-mp="12yz5emb3x47m">
            {bank.split(' ').map(w => w[0]).join('').slice(0, 2)}
          </span>
        </div>;
    }
  };
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  const handleSelectRecipient = (recipient: Recipient) => {
    setSelectedRecipient(recipient);
    setNewPayeeBank(recipient.bank || '');
    setNewPayeeAccount(recipient.accountNumber || '');
    setNewPayeeName(recipient.name || '');
    setSelectedBankForPayment(recipient.bank || '');
    setCameFromSavedPayee(true);
    setIsOwnAccountTransfer(false);
    setAmount('');
    if (recipient.bank === 'Bank of Punjab' || recipient.bank === 'RAAST') {
      setPaymentTab('raast');
    } else {
      setPaymentTab('other');
    }
    navigateTo('add_Payee');
  };
  useEffect(() => {
    if (step === 'processing') {
      const timer = setTimeout(() => navigateTo('success'), 3000);
      return () => clearTimeout(timer);
    }
  }, [step]);
  useEffect(() => {
    if (step === 'mpin') {
      setMpin('');
      setBiometricPhase('scanning');
      const currentScanType = biometricCounter % 2 === 0 ? 'face' : 'thumb';
      setScanType(currentScanType);
      biometricCounter++;
      const timeout1 = setTimeout(() => {
        setBiometricPhase('verified');
        const timeout2 = setTimeout(() => navigateTo('processing'), 800);
        return () => clearTimeout(timeout2);
      }, 2000);
      return () => clearTimeout(timeout1);
    }
  }, [step]);
  useEffect(() => {
    if (step === 'verify_Payee') {
      const t = setTimeout(() => navigateTo('Payee_saved'), 3000);
      return () => clearTimeout(t);
    }
  }, [step]);
  useEffect(() => {
    if (step === 'review') {
      if (isOwnAccountTransfer) {
        setPurpose('Transfer to own account');
      } else {
        setPurpose(prev => prev === 'Transfer to own account' ? 'Other' : prev);
      }
    }
  }, [step]);
  const renderStep = () => {
    switch (step) {
      // ─── SELECT RECIPIENT ────────────────────────────────────────────────────
      case 'select_recipient':
        return <motion.div key="select_recipient" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3107" data-mp="2moc2hwj7cl3x">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3108" data-mp="23vq9djzoj8n1">
              <button onClick={onBack} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} aria-label="Back" data-id="element-3109" data-mp="3ab0oatbc0xpt">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-3110" data-mp="3pe24rn7z70il" />
                <span className={`text-[16px] font-regular ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3111" data-mp="3kh1uf043fblo">
                  Send Money
                </span>
              </button>
              <div className="flex-1" data-id="element-3112" data-mp="3bmx7grs2ykbf" />
              <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-3113" data-mp="36oc4wlpcvojt">
                <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-3114" data-mp="2wpwg4pdswrdh" />
              </button>
            </div>
            <div className="px-6 mt-6 space-y-6" data-id="element-3115" data-mp="2soufc448k79e">
              <div data-id="element-3116" data-mp="2sq5iq2trrczz">
                <h3 className={`text-[13px] font-medium tracking-wider mb-4 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-3117" data-mp="2sqnk1dvagher">
                  Quick Links
                </h3>
                <div className="flex gap-4 items-start" data-id="element-3118" data-mp="2kfx0zhsyint7">
                  <button onClick={() => {
                  setNewPayeeBank('Bank of Punjab');
                  setSelectedQuickBank('Bank of Punjab');
                  setNewPayeeAccount('');
                  setNewPayeeName('');
                  setAccountInputType('account');
                  setAddPayeePhase('entry');
                  setCameFromQuickLink(true);
                  setCameFromSavedPayee(false);
                  setIsOwnAccountTransfer(false);
                  setSelectedBankForPayment('Bank of Punjab');
                  navigateTo('add_Payee');
                }} className="flex flex-col items-center gap-2 min-w-[68px]" data-id="element-3119" data-mp="2kedl3sb7qery">
                    <div className={`w-[68px] h-[68px] rounded-full flex items-center justify-center shadow-sm border ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.15)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-3120" data-mp="1iquqibs4hz6v">
                      <img src="/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP" className="w-10 h-10 object-contain" data-id="element-3121" data-mp="1f6u7ddfg7g8m" />
                    </div>
                    <span className={`text-[12px] font-medium ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3122" data-mp="1k802tj0hthlu">
                      BOP
                    </span>
                  </button>
                  <button onClick={() => {
                  setNewPayeeBank('Jazz Cash');
                  setSelectedQuickBank('Jazz Cash');
                  setNewPayeeAccount('');
                  setNewPayeeName('');
                  setAccountInputType('account');
                  setAddPayeePhase('entry');
                  setCameFromQuickLink(true);
                  setCameFromSavedPayee(false);
                  setIsOwnAccountTransfer(false);
                  setSelectedBankForPayment('Jazz Cash');
                  navigateTo('add_Payee');
                }} className="flex flex-col items-center gap-2 min-w-[68px]" data-id="element-3123" data-mp="rk4aeqhyibuv">
                    <div className={`w-[68px] h-[68px] rounded-full flex items-center justify-center shadow-sm border ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.15)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-3124" data-mp="3tf6pdljze9vb">
                      <svg viewBox="0 0 40 40" className="w-9 h-9" fill="none" data-id="element-3125" data-mp="b65vaykrac33">
                        <path d="M8 20C8 14 12 8 20 8C24 8 27 10 29 13L25 17C23.5 15 21.8 14 20 14C15.6 14 13 17 13 20C13 23 15.6 26 20 26C21.8 26 23.5 25 25 23L29 27C27 30 24 32 20 32C12 32 8 26 8 20Z" fill="#DC2626" data-id="element-3126" data-mp="fxe5vxxto3hu" />

                        <path d="M28 12C30 14 32 17 32 20C32 23 30 26 28 28C26 26 24 23 24 20C24 17 26 14 28 12Z" fill="#FBBF24" data-id="element-3127" data-mp="tx74efflbo0d" />
                      </svg>
                    </div>
                    <span className={`text-[12px] font-medium ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3128" data-mp="2ag8tt361pg6i">
                      JazzCash
                    </span>
                  </button>
                  <button onClick={() => {
                  setNewPayeeBank('Easypaisa');
                  setSelectedQuickBank('Easypaisa');
                  setNewPayeeAccount('');
                  setNewPayeeName('');
                  setAccountInputType('account');
                  setAddPayeePhase('entry');
                  setCameFromQuickLink(true);
                  setCameFromSavedPayee(false);
                  setIsOwnAccountTransfer(false);
                  setSelectedBankForPayment('Easypaisa');
                  navigateTo('add_Payee');
                }} className="flex flex-col items-center gap-2 min-w-[68px]" data-id="element-3129" data-mp="2y48h1t45eogc">
                    <div className={`w-[68px] h-[68px] rounded-full flex items-center justify-center shadow-sm border ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.15)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-3130" data-mp="3oq2d42sbz3qe">
                      <svg viewBox="0 0 40 40" className="w-9 h-9" fill="none" data-id="element-3131" data-mp="gbkaigl5sge6">
                        <circle cx="20" cy="20" r="14" stroke="#22C55E" strokeWidth="3" fill="none" data-id="element-3132" data-mp="gdofz2yphngk" />

                        <path d="M12 20C12 15.6 15.6 12 20 12" stroke="#1A1A1A" strokeWidth="3" strokeLinecap="round" data-id="element-3133" data-mp="2ick2s8tdqy2" />
                      </svg>
                    </div>
                    <span className={`text-[12px] font-medium ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3134" data-mp="2d0ftkvjhyrqp">
                      EasyPaisa
                    </span>
                  </button>
                  <button onClick={() => {
                  setNewPayeeBank('');
                  setNewPayeeAccount('');
                  setNewPayeeName('');
                  setAccountInputType('account');
                  setAddPayeePhase('entry');
                  setCameFromQuickLink(false);
                  setCameFromSavedPayee(false);
                  setIsOwnAccountTransfer(false);
                  setSelectedBankForPayment('');
                  navigateTo('add_Payee');
                }} className="flex flex-col items-center gap-2 min-w-[68px]" data-id="element-3135" data-mp="2qynjwkm12usw">
                    <div className="w-[68px] h-[68px] rounded-full bg-[#F37024] flex items-center justify-center shadow-md" data-id="element-3136" data-mp="3jkp0qpf6nksg">
                      <PlusIcon className="w-8 h-8 text-white" strokeWidth={2} data-id="element-3137" data-mp="1elqt8po4wgpz" />
                    </div>
                    <span className={`text-[12px] font-medium ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-3138" data-mp="1tc6g08w77wq9">
                      New Transfer
                    </span>
                  </button>
                </div>
              </div>
              <div data-id="element-3139" data-mp="1y96i68pxgfc3">
                <input type="text" placeholder="Search name, bank or account numb" className={`w-full rounded-2xl py-4 px-4 text-[15px] outline-none border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#F0E8E3]'}`} data-id="element-3140" data-mp="2c97lrvxku0v9" />
              </div>
              <button onClick={() => {
              setNewPayeeBank('Bank of Punjab');
              setNewPayeeAccount('');
              setNewPayeeName('Own Account');
              setAccountInputType('account');
              setAddPayeePhase('entry');
              setCameFromQuickLink(false);
              setCameFromSavedPayee(false);
              setIsOwnAccountTransfer(true);
              setOwnReceivingAccount(OWN_ACCOUNTS[1]);
              setPayFromAccount(PAY_FROM_ACCOUNTS[0]);
              setSelectedBankForPayment('');
              navigateTo('add_Payee');
            }} className="w-full rounded-xl py-4 px-4 text-center text-[15px] font-regular border-2 transition-all active:scale-[0.98]" style={{
              background: isDark ? 'transparent' : '#FFFFFF',
              borderColor: '#F37024',
              color: '#F37024'
            }} data-id="element-3141" data-mp="2oix8l99wll6o">
                Transfer to own Accounts
              </button>
              <div data-id="element-3142" data-mp="1v3js4la7evrr">
                <h3 className={cn(`text-[13px] font-medium tracking-wider mb-4 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`, 'font-[500]')} data-id="element-3143" data-mp="1r42kpmb7vojy">
                  All Payees
                </h3>
                <div className="space-y-3" data-id="element-3144" data-mp="1h88yfb7p4goz">
                  {Payees.map(rec => {
                  const Icon = rec.icon;
                  const pastPayments = PAST_PAYMENTS[rec.id] || [];
                  const isExpanded = expandedPayee === rec.id;
                  return <motion.div key={rec.id} layout className={`w-full rounded-2xl shadow-sm overflow-hidden ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white border border-[#F0E8E3]'}`} data-id="element-3145" data-mp="13c3wy2ul00px">
                        <button onClick={() => handleSelectRecipient(rec)} className="w-full flex items-center gap-4 text-left p-4" data-id="element-3146" data-mp="edami0rwtyh9">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-3147" data-mp="obq2b7lhllbg">
                            <Icon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-3148" data-mp="3rsp0ellrja8x" />
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col justify-center" data-id="element-3149" data-mp="5kp3qkoa3rsf">
                            <h4 className={`text-[16px] font-bold truncate ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} style={{
                          fontFamily: 'Poppins, sans-serif'
                        }} data-id="element-3150" data-mp="ac76ne9v42g1">
                              {rec.nickname}
                            </h4>
                            <p className="text-[13px] font-medium truncate text-[#5A5B5E]" style={{
                          fontFamily: 'Poppins, sans-serif'
                        }} data-id="element-3151" data-mp="311j78m01hmv5">
                              {rec.name}
                            </p>
                            <p className="text-[12px] truncate text-[#818285]" style={{
                          fontFamily: 'Poppins, sans-serif'
                        }} data-id="element-3152" data-mp="2n3b3nv8hznb0">
                              {rec.accountNumber}
                            </p>
                          </div>
                          <ChevronRightIcon className={`w-5 h-5 shrink-0 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3153" data-mp="c7ccwm00e9xh" />
                        </button>
                        <div className={`mx-4 h-px ${isDark ? 'bg-[rgba(243,112,36,0.1)]' : 'bg-[#F0E8E3]'}`} data-id="element-3154" data-mp="evoax1nrjn6h" />

                        <button onClick={() => setExpandedPayee(isExpanded ? null : rec.id)} className={`w-full flex items-center justify-end gap-1.5 px-4 py-2.5 transition-colors ${isDark ? 'hover:bg-[#3D2518]/50' : 'hover:bg-[#FFF7F0]'}`} data-id="element-3155" data-mp="5u8ddui1rfjo">
                          <span className={`text-[12px] font-medium ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3156" data-mp="osauln4cs86x">
                            Past Payments
                          </span>
                          <motion.div animate={{
                        rotate: isExpanded ? 180 : 0
                      }} transition={{
                        duration: 0.2
                      }} data-id="element-3157" data-mp="1ghnq0tt0tvqu">
                            <ChevronDownIcon className={`w-4 h-4 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3158" data-mp="rqgevy1zyoow" />
                          </motion.div>
                        </button>
                        <AnimatePresence initial={false} data-id="element-3159" data-mp="1fgub80rm79pa">
                          {isExpanded && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.25,
                        ease: 'easeOut'
                      }} className="overflow-hidden" data-id="element-3160" data-mp="1aju5xs2laaz8">
                              <div className={`mx-4 mb-1 h-px ${isDark ? 'bg-[rgba(243,112,36,0.08)]' : 'bg-[#F5EDEA]'}`} data-id="element-3161" data-mp="2fhz3vguxftgp" />

                              {pastPayments.length === 0 ? <div className="px-4 py-4 text-center" data-id="element-3162" data-mp="2okgrnzq8cu5t">
                                  <p className={`text-[12px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3163" data-mp="2ti05iyj0n4x8">
                                    No past payments found
                                  </p>
                                </div> : <div className="px-4 pb-3 pt-1 space-y-0" data-id="element-3164" data-mp="polxhn38mhjv">
                                  {pastPayments.map((txn, idx) => <div key={idx} data-id="element-3165" data-mp="uglsr28d4ma4" data-mpi={idx}>
                                      <div className="flex items-center justify-between py-2.5" data-id="element-3166" data-mp="fqellint2poq">
                                        <div className="flex items-center gap-3" data-id="element-3167" data-mp="kozk9dnet1b2">
                                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${txn.status === 'Success' ? 'bg-[#22C55E]/10' : 'bg-[#EF4444]/10'}`} data-id="element-3168" data-mp="kmwose3hmfyy">
                                            {txn.status === 'Success' ? <CheckCircleIcon className="w-4 h-4 text-[#22C55E]" strokeWidth={2} data-id="element-3169" data-mp="13efl6230avs0" /> : <XIcon className="w-4 h-4 text-[#EF4444]" strokeWidth={2.5} data-id="element-3170" data-mp="266rxbz2582s2" />}
                                          </div>
                                          <div data-id="element-3171" data-mp="1nhv47yihfgbf">
                                            <p className={`text-[12px] font-semibold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3172" data-mp="1nimuqux82nuu">
                                              PKR{' '}
                                              {txn.amount.toLocaleString('en-US')}
                                            </p>
                                            <p className={`text-[11px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3173" data-mp="zrr96fjd0ow1">
                                              {txn.date}
                                            </p>
                                          </div>
                                        </div>
                                        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${txn.status === 'Success' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`} data-id="element-3174" data-mp="3dl5amhziede8">
                                          {txn.status}
                                        </span>
                                      </div>
                                      {idx < pastPayments.length - 1 && <div className={`h-px ${isDark ? 'bg-[rgba(243,112,36,0.06)]' : 'bg-[#F5EDEA]'}`} data-id="element-3175" data-mp="58g7nr4j9a0i" />}
                                    </div>)}
                                </div>}
                            </motion.div>}
                        </AnimatePresence>
                      </motion.div>;
                })}
                </div>
              </div>
            </div>
            <AnimatePresence data-id="element-3176" data-mp="16x51b2u28b07">
              {deleteCandidate && <>
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} onClick={() => setDeleteCandidate(null)} className="fixed inset-0 bg-black/50 z-[110] backdrop-blur-sm" data-id="element-3177" data-mp="11ybl32ne25wc" />

                  <motion.div initial={{
                opacity: 0,
                scale: 0.9,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.9,
                y: 20
              }} transition={{
                type: 'spring',
                damping: 22,
                stiffness: 300
              }} className={`fixed inset-x-6 top-1/2 -translate-y-1/2 z-[111] max-w-[360px] mx-auto rounded-3xl p-6 shadow-2xl ${isDark ? 'bg-[#2D1810]' : 'bg-white'}`} data-id="element-3178" data-mp="1p0lmp0j8tmaf">
                    <div className="w-14 h-14 rounded-full bg-[#EF4444]/10 flex items-center justify-center mx-auto mb-4" data-id="element-3179" data-mp="rcbokwmkviol">
                      <AlertTriangleIcon className="w-7 h-7 text-[#EF4444]" strokeWidth={2} data-id="element-3180" data-mp="7prry9hyjjop" />
                    </div>
                    <h3 className={`text-[18px] font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3181" data-mp="hp8vak9qllhi">
                      Delete Payee?
                    </h3>
                    <p className={`text-[14px] text-center mb-6 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3182" data-mp="qo2zowtb7g5m">
                      Are you sure you want to delete{' '}
                      <span className={`font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3183" data-mp="10ep5jzb84sb4">
                        {deleteCandidate.name}
                      </span>
                      ? This action cannot be undone.
                    </p>
                    <div className="space-y-3 flex flex-col items-center" data-id="element-3184" data-mp="15fv4ievh9bt3">
                      {/* ── Delete confirm CTA ── */}
                      <button onClick={confirmDeletePayee} style={{
                    ...ctaStyle,
                    background: '#EF4444'
                  }} data-id="element-3185" data-mp="1cfnlbxlsrmv2">
                        Yes, Delete
                      </button>
                      <button onClick={() => setDeleteCandidate(null)} style={{
                    ...ctaStyle,
                    background: isDark ? 'rgba(255,255,255,0.05)' : '#F0E8E3',
                    color: isDark ? '#FFFFFF' : '#303030'
                  }} data-id="element-3186" data-mp="1w60mv540dk28">
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                </>}
            </AnimatePresence>
            <AnimatePresence data-id="element-3187" data-mp="888cdvv87lyl">
              {PayeeToast && <motion.div initial={{
              opacity: 0,
              y: 40
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 40
            }} className="fixed inset-x-6 bottom-8 z-[112] max-w-[360px] mx-auto" data-id="element-3188" data-mp="89jdtp178hmj">
                  <div className="flex items-center gap-3 px-4 py-3.5 rounded-2xl shadow-lg bg-[#22C55E] text-white" data-id="element-3189" data-mp="m8j38ywjd0jr">
                    <CheckCircleIcon className="w-5 h-5 shrink-0" strokeWidth={2} data-id="element-3190" data-mp="h6tp3ae0o6rg" />

                    <p className="text-[14px] font-semibold" data-id="element-3191" data-mp="31ydrl9hdlyrx">{PayeeToast}</p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>;
      // ─── SELECT BANK ─────────────────────────────────────────────────────────
      case 'select_bank':
        const sortedBanks = (() => {
          const bop = BANKS.filter(b => b === 'Bank of Punjab');
          const rest = BANKS.filter(b => b !== 'Bank of Punjab').sort((a, b) => a.localeCompare(b));
          return [...bop, ...rest];
        })();
        const filteredBanks = sortedBanks.filter(bank => bank.toLowerCase().includes(bankSearch.toLowerCase()));
        return <motion.div key="select_bank" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3192" data-mp="e7d1t1ur4mjf">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3193" data-mp="s6cmxzj22jau">
              <button onClick={() => {
              setBankSearch('');
              navigateTo('add_Payee', -1);
            }} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} aria-label="Back" data-id="element-3194" data-mp="2hnzvokmt3uit">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-3195" data-mp="2mgsxazmzamqo" />
                <span className={`text-[16px] font-regular ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3196" data-mp="1ypdt11bn5vcs">
                  Select Bank
                </span>
              </button>
              <div className="flex-1" data-id="element-3197" data-mp="28p4mr0sdo6bt" />
              <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-3198" data-mp="28m0dhp022kea">
                <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-3199" data-mp="3k5crn8va7tqn" />
              </button>
            </div>
            <div className="px-6 mt-6 flex-1 flex flex-col pb-8" data-id="element-3200" data-mp="3f6i11kr2p0vz">
              <div className="mb-6" data-id="element-3201" data-mp="19qf0x4e8wi6p">
                <input type="text" value={bankSearch} onChange={e => setBankSearch(e.target.value)} placeholder="Search bank name" autoFocus className={`w-full rounded-2xl py-4 px-4 text-[15px] outline-none border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#F0E8E3]'}`} data-id="element-3202" data-mp="19pn7o084ewjl" />
              </div>
              <div className="space-y-3 flex-1 overflow-y-auto" data-id="element-3203" data-mp="zu28e9ufsadj">
                {filteredBanks.map(bank => <button key={bank} onClick={() => {
                setSelectedBankForPayment(bank);
                setNewPayeeBank(bank);
                setBankSearch('');
                navigateTo('add_Payee', -1);
              }} className={`w-full rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow active:scale-[0.98] ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white border border-[#F0E8E3]'}`} data-id="element-3204" data-mp="uto2axcebai5">
                    <div className="w-11 h-11 shrink-0" data-id="element-3205" data-mp="29ra20sgv6mqb">
                      {renderBankLogo(bank)}
                    </div>
                    <span className={`text-[15px] font-semibold flex-1 text-left ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3206" data-mp="2xip90yjl5s7w">
                      {bank}
                    </span>
                    <ChevronRightIcon className={`w-5 h-5 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3207" data-mp="2nrb60gsfeaa0" />
                  </button>)}
                {filteredBanks.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-3208" data-mp="3l7gxbeu21qfe">
                    <SearchIcon className={`w-10 h-10 mb-3 ${isDark ? 'text-[#B8A89E]/40' : 'text-[#818285]/40'}`} data-id="element-3209" data-mp="3v3h09mxrsgfs" />

                    <p className={`text-[15px] font-medium ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3210" data-mp="3q1rmhzj5xoo0">
                      No banks found
                    </p>
                  </div>}
              </div>
            </div>
          </motion.div>;
      // ─── ADD Payee (PAYMENT DETAILS) ────────────────────────────────────────
      case 'add_Payee':
        return <motion.div key="add_Payee" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3211" data-mp="32di8vkw3s77y">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3212" data-mp="2ofjysrotewy9">
              <button onClick={() => {
              setCameFromSavedPayee(false);
              setCameFromQuickLink(false);
              setIsOwnAccountTransfer(false);
              navigateTo('select_recipient', -1);
            }} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} aria-label="Back" data-id="element-3213" data-mp="18vj9959ebzg2">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-3214" data-mp="p6s9j2fqpstc" />
                <span className={`text-[16px] font-regular ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3215" data-mp="u6wv36vi9dqf">
                  Payment Details
                </span>
              </button>
              <div className="flex-1" data-id="element-3216" data-mp="1ir96gsptip0" />
              <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-3217" data-mp="1hpm1dyfd5qx">
                <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-3218" data-mp="293zetqtvwbn7" />
              </button>
            </div>
            <div className="px-6 mt-2 flex-1 flex flex-col pb-8" data-id="element-3219" data-mp="246hdali74yp8">
              <div className="space-y-5" data-id="element-3220" data-mp="2hx2zaj2y5orc">
                <div data-id="element-3221" data-mp="2hxw0l4j4zm23">
                  <div className="flex items-center gap-1.5 mb-2" data-id="element-3222" data-mp="2d5kypw4u5q6w">
                    <p className={cn(`text-[13px] font-bold ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`, 'font-[500]')} data-id="element-3223" data-mp="2d38aj18hijle">
                      {isOwnAccountTransfer ? 'Pay To' : 'Account Number or IBAN'}
                    </p>
                    {!isOwnAccountTransfer && <button type="button" onClick={() => setShowAccountInfo(v => !v)} aria-label="What is an IBAN?" className="flex items-center justify-center" style={{
                    color: isDark ? '#F37024' : '#661806'
                  }} data-id="element-3224" data-mp="16g4dh0b0ndiu">
                        <InfoIcon className="w-4 h-4" strokeWidth={2} data-id="element-3225" data-mp="ikhttyn85cmc" />
                      </button>}
                  </div>
                  {!isOwnAccountTransfer && showAccountInfo && <div className="mb-2 p-3 rounded-lg relative" style={{
                  background: isDark ? '#3D2518' : '#FFF3EC',
                  border: isDark ? '1px solid rgba(243,112,36,0.2)' : '1px solid rgba(102,24,6,0.15)'
                }} data-id="element-3226" data-mp="8mlfyz2wk3kj">
                      <p style={{
                    fontFamily: 'Poppins, system-ui, sans-serif',
                    fontWeight: 500,
                    fontSize: 12,
                    color: isDark ? '#FFFFFF' : '#661806',
                    marginBottom: 4
                  }} data-id="element-3227" data-mp="1u2105zw3pvp4">
                        IBAN is 24 characters
                      </p>
                      <p style={{
                    fontFamily: 'Poppins, system-ui, sans-serif',
                    fontWeight: 400,
                    fontSize: 11,
                    lineHeight: 1.5,
                    color: isDark ? '#B8A89E' : '#6E6F72'
                  }} data-id="element-3228" data-mp="1ilcu5ki0rd4w">
                        A Pakistani IBAN is exactly 24 characters long, starting
                        with PK, followed by 2 check digits, a 4-digit bank
                        identifier, and your 16-digit account number.
                      </p>
                    </div>}
                  {isOwnAccountTransfer ? <div className="relative" data-id="element-3229" data-mp="31kas29jfgiwv">
                      <button type="button" onClick={() => setShowOwnReceivingDropdown(!showOwnReceivingDropdown)} className={`w-full rounded-xl px-4 py-3.5 flex items-center justify-between border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#E5E5E5]'} ${sameAccountError ? 'border-red-400' : ''}`} data-id="element-3230" data-mp="31llwmbkp5up1">
                        <div className="text-left min-w-0" data-id="element-3231" data-mp="3pgycmaga8bcs">
                          <p className={`text-[14px] font-bold truncate ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3232" data-mp="3u8q2eirm6d1v">
                            {ownReceivingAccount.holder}
                          </p>
                          <p className={`text-[12px] truncate ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3233" data-mp="832w9jdyeda1">
                            {ownReceivingAccount.label}{' '}
                            <span data-id="element-3234" data-mp="tcgnnytik0w4">{ownReceivingAccount.number}</span>
                          </p>
                        </div>
                        <ChevronDownIcon className={`w-5 h-5 transition-transform ${showOwnReceivingDropdown ? 'rotate-180' : ''} ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3235" data-mp="17crckwb9j5en" />
                      </button>
                      <AnimatePresence data-id="element-3236" data-mp="el7r15r71f9h">
                        {showOwnReceivingDropdown && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} className={`absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden shadow-lg z-30 ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white border border-[#E5E5E5]'}`} data-id="element-3237" data-mp="9mmp91wamnix">
                            {OWN_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                        setOwnReceivingAccount(acc);
                        setShowOwnReceivingDropdown(false);
                      }} className={`w-full text-left p-4 flex items-center justify-between transition-colors ${isDark ? 'hover:bg-[#3D2518]' : 'hover:bg-[#FFF7F0]'} ${ownReceivingAccount.id === acc.id ? isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F0]' : ''}`} data-id="element-3238" data-mp="2lbt8frnjattp">
                                <div className="min-w-0" data-id="element-3239" data-mp="15sptdb92hnz3">
                                  <p className={`text-[14px] font-bold truncate ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3240" data-mp="3x0zvi5buo9u">
                                    {acc.holder}
                                  </p>
                                  <p className={`text-[12px] truncate ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3241" data-mp="hwk625ekvpji">
                                    {acc.label} {acc.number}
                                  </p>
                                </div>
                                {ownReceivingAccount.id === acc.id && <CheckIcon className="w-5 h-5 text-[#F37024] shrink-0" strokeWidth={2.5} data-id="element-3242" data-mp="3v55cn7zt4zg1" />}
                              </button>)}
                          </motion.div>}
                      </AnimatePresence>
                      {sameAccountError && <p className="text-red-500 text-[11px] mt-1.5 ml-1" data-id="element-3243" data-mp="2m3d7dvmnvsqe">
                          Source and destination accounts cannot be the same.
                        </p>}
                    </div> : <div className="relative" data-id="element-3244" data-mp="2c70egzyxxiwp">
                      <input type="text" inputMode="text" value={newPayeeAccount} onFocus={() => setShowAccountInfo(false)} onChange={e => {
                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 24);
                    setNewPayeeAccount(val);
                    if (val.toLowerCase().startsWith('pkbpun')) {
                      setSelectedBankForPayment('Bank of Punjab');
                      setNewPayeeBank('Bank of Punjab');
                    }
                  }} maxLength={24} placeholder="Enter details" className={`w-full rounded-xl py-4 pl-4 pr-4 text-[15px] outline-none border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#E5E5E5]'}`} data-id="element-3245" data-mp="27ez5bmx2k8aw" />
                    </div>}
                </div>
                {!isOwnAccountTransfer && <div data-id="element-3246" data-mp="1sqyun071ll3d">
                    <p className={`text-[13px] font-medium mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-3247" data-mp="1sqfk44jq1yzd">
                      Bank
                    </p>
                    <button onClick={() => navigateTo('select_bank')} className={`w-full rounded-xl py-3.5 px-4 flex items-center justify-between border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#E5E5E5]'}`} data-id="element-3248" data-mp="2l9mde8ykgo1a">
                      <div className="flex items-center gap-3" data-id="element-3249" data-mp="jl8ekvhk9nsd">
                        {selectedBankForPayment ? <div className="w-8 h-8 shrink-0" data-id="element-3250" data-mp="3mzuh2tj5p3ub">
                            {renderBankLogo(selectedBankForPayment)}
                          </div> : <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-[#2D1810]' : 'bg-[#F0F0F0]'}`} data-id="element-3251" data-mp="3w0kchw4i8s7i">
                            <BuildingIcon className={`w-4 h-4 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3252" data-mp="1babg43i2ck1b" />
                          </div>}
                        <span className={`text-[15px] ${selectedBankForPayment ? isDark ? 'text-white' : 'text-[#303030]' : isDark ? 'text-[#B8A89E]/70' : 'text-[#818285]'}`} data-id="element-3253" data-mp="22jep04w8pibh">
                          {selectedBankForPayment || 'Select Bank'}
                        </span>
                      </div>
                      <ChevronRightIcon className={`w-5 h-5 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3254" data-mp="2ggl2pl23wqtu" />
                    </button>
                  </div>}
                <div data-id="element-3255" data-mp="2uacdewrkqgr5">
                  <p className={`text-[13px] font-medium mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-3256" data-mp="2u9subcq6tmbj">
                    Pay From
                  </p>
                  <div className="relative" data-id="element-3257" data-mp="349bbjlepqtzw">
                    <button type="button" onClick={() => setShowPayFromDropdown(!showPayFromDropdown)} className={`w-full rounded-xl px-4 py-3.5 flex items-center justify-between border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#E5E5E5]'} ${sameAccountError ? 'border-red-400' : ''}`} data-id="element-3258" data-mp="3465oebpna26t">
                      <div className="text-left min-w-0 flex items-center gap-3" data-id="element-3259" data-mp="inse964euifh">
                        {payFromAccount.isCard && <CreditCardIcon className="w-4 h-4 text-[#F37024] shrink-0" data-id="element-3260" data-mp="1gck9e8vkb4aa" />}
                        <div data-id="element-3261" data-mp="1bdqvwgg9zda4">
                          <p className={`text-[14px] font-bold truncate ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3262" data-mp="16k62z2tjh9hs">
                            {payFromAccount.holder}
                          </p>
                          <p className={`text-[12px] truncate ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3263" data-mp="1z321cb8klvul">
                            {payFromAccount.label}{' '}
                            <span data-id="element-3264" data-mp="3tgxq0bycf4as">{payFromAccount.number}</span>
                          </p>
                        </div>
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 transition-transform ${showPayFromDropdown ? 'rotate-180' : ''} ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3265" data-mp="3errt4umz9q62" />
                    </button>
                    <AnimatePresence data-id="element-3266" data-mp="39sye2xs1lr3s">
                      {showPayFromDropdown && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} className={`absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden shadow-lg z-30 ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white border border-[#E5E5E5]'}`} data-id="element-3267" data-mp="2vtf6c6358hs0">
                          {PAY_FROM_ACCOUNTS.filter(acc => !(acc.isCard && (paymentTab === 'raast' || selectedBankForPayment === 'RAAST'))).map(acc => <button key={acc.id} onClick={() => {
                        setPayFromAccount(acc);
                        setShowPayFromDropdown(false);
                      }} className={`w-full text-left p-4 flex items-center justify-between transition-colors ${isDark ? 'hover:bg-[#3D2518]' : 'hover:bg-[#FFF7F0]'} ${payFromAccount.id === acc.id ? isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F0]' : ''}`} data-id="element-3268" data-mp="3c5imp3g0ye3w">
                              <div className="flex items-center gap-3 min-w-0" data-id="element-3269" data-mp="10rwu1f16vqtj">
                                {acc.isCard && <CreditCardIcon className="w-4 h-4 text-[#F37024] shrink-0" data-id="element-3270" data-mp="15saplxllt8ao" />}
                                <div className="min-w-0" data-id="element-3271" data-mp="d1amrpz0eakl">
                                  <p className={`text-[14px] font-bold truncate ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3272" data-mp="d3wtjne2sm1r">
                                    {acc.holder}
                                  </p>
                                  <p className={`text-[12px] truncate ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3273" data-mp="1tfsnmavgp2m3">
                                    {acc.label} {acc.number}
                                  </p>
                                </div>
                              </div>
                              {payFromAccount.id === acc.id && <CheckIcon className="w-5 h-5 text-[#F37024] shrink-0" strokeWidth={2.5} data-id="element-3274" data-mp="348b930dk9jf3" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                    {sameAccountError && <p className="text-red-500 text-[11px] mt-1.5 ml-1" data-id="element-3275" data-mp="2lah2eszwnjzv">
                        Source and destination accounts cannot be the same.
                      </p>}
                  </div>
                </div>
                {payFromAccount.isCard && <p className="text-[10px] mt-[0px]" style={{
                color: '#9E9E9E',
                marginTop: 0
              }} data-id="element-3276" data-mp="1csx1gfrvwm0y">
                    Cash Advance charges may apply
                  </p>}
                <div className="flex flex-col items-center mt-6 mb-2" data-id="element-3277" data-mp="y2ptwv3f0nf1">
                  <div className="flex items-baseline gap-2 justify-center w-full" data-id="element-3278" data-mp="t561mr9vua43">
                    <span className={`text-[20px] font-medium ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3279" data-mp="p32ozzbh8zh8">
                      PKR
                    </span>
                    <input type="text" inputMode="numeric" value={amount ? Number(amount.replace(/,/g, '')).toLocaleString('en-US') : ''} onChange={e => {
                    const raw = e.target.value.replace(/[^\d]/g, '');
                    setAmount(raw);
                  }} placeholder="0" className={`text-[56px] font-medium bg-transparent outline-none text-center tracking-tight ${isDark ? 'text-white placeholder:text-white/30' : 'text-[#5A1408] placeholder:text-[#5A1408]/30'}`} style={{
                    width: amount ? `${Math.max(amount.length, 1) * 36 + 20}px` : '80px',
                    maxWidth: '100%',
                    minWidth: '80px'
                  }} data-id="element-3280" data-mp="f551xf5ct8q7" />
                  </div>
                  <div className={`w-[80%] max-w-[260px] h-px mt-1 ${isDark ? 'bg-[#3D2518]' : 'bg-[#E5E5E5]'}`} data-id="element-3281" data-mp="3jfunry34t64c" />

                  {(() => {
                  const enteredAmount = Number(amount.replace(/,/g, '')) || 0;
                  const balance = payFromAccount.isCard ? 0 : payFromAccount.balance;
                  const remaining = balance - enteredAmount;
                  const insufficient = !payFromAccount.isCard && remaining < 0;
                  return <div className={`text-[13px] mt-3 flex flex-col items-center gap-1 ${isDark ? 'text-[#B8A89E]' : 'text-[#1A1A1A]'}`} data-id="element-3282" data-mp="15n0153hc5pvy">
                        {payFromAccount.isCard ? <span className={insufficient ? 'text-red-500' : 'text-[#818285]'} data-id="element-3283" data-mp="1egd30uxdo1u6">
                            Credit Limit: PKR{' '}
                            <span className="text-[#F37024]" data-id="element-3284" data-mp="hxxefh7uh6ul">50,000</span>
                            {'  |  '}Remaining: PKR{' '}
                            <span className={enteredAmount > 50000 ? 'text-red-500' : 'text-[#F37024]'} data-id="element-3285" data-mp="7syrew65zr43">
                              {(50000 - enteredAmount).toLocaleString('en-US')}
                            </span>
                          </span> : <>
                            <p data-id="element-3286" data-mp="vi06v3df02iv">
                              Available Balance: PKR{' '}
                              {balance.toLocaleString('en-US')}
                            </p>
                            {enteredAmount > 0 && <p data-id="element-3287" data-mp="lmygy5bbewf8">
                                Remaining Balance: PKR{' '}
                                <span className={insufficient ? 'text-red-500' : 'text-[#F37024]'} data-id="element-3288" data-mp="31emcbwaokgvx">
                                  {remaining.toLocaleString('en-US')}
                                </span>
                              </p>}
                          </>}
                      </div>;
                })()}
                </div>
                {!isOwnAccountTransfer && <div className="mt-2" data-id="element-3289" data-mp="4dfgnt4rh6k4">
                    <div className={`flex rounded-2xl overflow-hidden border ${isDark ? 'border-[rgba(243,112,36,0.15)] bg-[#2D1810]' : 'border-[#E5E5E5] bg-[#F5F5F5]'}`} data-id="element-3290" data-mp="3vixa6m2znds9">
                      <button onClick={() => setPaymentTab('raast')} className={`flex-1 flex items-center justify-center gap-2 py-3.5 transition-all ${paymentTab === 'raast' ? isDark ? 'bg-[#3D2518]' : 'bg-white shadow-sm' : ''}`} data-id="element-3291" data-mp="2k4jsy02qpwl7">
                        <div className={cn(`w-7 h-7 rounded flex items-center justify-center ${paymentTab === 'raast' ? 'bg-[#E8F5E9]' : isDark ? 'bg-[#2D1810]' : 'bg-[#EBEBEB]'}`, 'bg-[#ffffff]')} data-id="element-3292" data-mp="zl7xjzohte07">
                          <img src="/pasted-image.svg" alt="Raast" className="w-5 h-5 object-contain" data-id="element-3293" data-mp="1eam6x7s2jvfl" />
                        </div>
                        <span className={`text-[14px] font-bold ${paymentTab === 'raast' ? isDark ? 'text-white' : 'text-[#1A1A1A]' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3294" data-mp="1saes6nkttjg6">
                          Raast
                        </span>
                      </button>
                      <div className={`w-px self-stretch ${isDark ? 'bg-[rgba(243,112,36,0.15)]' : 'bg-[#E5E5E5]'}`} data-id="element-3295" data-mp="3m2dgb1s0e56x" />

                      <button onClick={() => setPaymentTab('other')} className={`flex-1 flex items-center justify-center gap-2 py-3.5 transition-all ${paymentTab === 'other' ? isDark ? 'bg-[#3D2518]' : 'bg-white shadow-sm' : ''}`} data-id="element-3296" data-mp="3vz8flmw3w9m9">
                        <div className={`w-7 h-7 rounded flex items-center justify-center ${paymentTab === 'other' ? 'bg-[#FFF3E0]' : isDark ? 'bg-[#2D1810]' : 'bg-[#EBEBEB]'}`} data-id="element-3297" data-mp="4tqi6iurdi8u">
                          <BuildingIcon className="w-4 h-4" style={{
                        color: paymentTab === 'other' ? '#F37024' : '#9E9E9E'
                      }} data-id="element-3298" data-mp="dmtsiia3xez9" />
                        </div>
                        <span className={`text-[14px] font-bold ${paymentTab === 'other' ? isDark ? 'text-white' : 'text-[#1A1A1A]' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3299" data-mp="173dsp2ud2b5r">
                          FT / IBFT
                        </span>
                      </button>
                    </div>
                    {paymentTab === 'raast' ? <p className={`text-[12px] mt-1.5 ml-1 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3300" data-mp="nfo9hk0hpj85">
                        Free of cost
                      </p> : <p className={`text-[12px] mt-1.5 ml-1 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3301" data-mp="jgyyfy2xcdro">
                        Charges may apply
                      </p>}
                  </div>}
              </div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mt-auto pt-8 flex justify-center" data-id="element-3302" data-mp="oypspynqwm46">
                {/* ── Continue CTA ── */}
                <button disabled={sameAccountError || !isOwnAccountTransfer && !newPayeeAccount.trim() || !isOwnAccountTransfer && !selectedBankForPayment.trim() || !amount || Number(amount.replace(/,/g, '')) <= 0 || (payFromAccount.isCard ? Number(amount.replace(/,/g, '')) > 50000 : Number(amount.replace(/,/g, '')) > payFromAccount.balance)} onClick={() => {
                const newPayee: Recipient = {
                  id: `temp-${Date.now()}`,
                  name: isOwnAccountTransfer ? ownReceivingAccount.label : newPayeeName || 'Recipient',
                  bank: isOwnAccountTransfer ? 'Bank of Punjab' : paymentTab === 'raast' ? 'RAAST' : newPayeeBank,
                  accountNumber: isOwnAccountTransfer ? ownReceivingAccount.number : newPayeeAccount,
                  icon: BuildingIcon,
                  color: '#F37024'
                };
                setSelectedRecipient(newPayee);
                setTransferMethod(paymentTab === 'raast' ? 'raast' : '1link');
                navigateTo('review');
              }} style={{
                ...ctaStyle,
                opacity: sameAccountError || !isOwnAccountTransfer && !newPayeeAccount.trim() || !isOwnAccountTransfer && !selectedBankForPayment.trim() || !amount || Number(amount.replace(/,/g, '')) <= 0 ? 0.5 : 1
              }} data-id="element-3303" data-mp="626j78914w73">
                  Continue
                </button>
              </motion.div>
            </div>
          </motion.div>;
      // ─── VERIFY Payee ───────────────────────────────────────────────────────
      case 'verify_Payee':
        return <motion.div key="verify_Payee" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3304" data-mp="efcok1pxw4fu">
            <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} className="mb-6" data-id="element-3305" data-mp="3r09othfad9l1">
              <ShieldIcon className="w-16 h-16 text-[#F37024]" strokeWidth={1.5} data-id="element-3306" data-mp="iq84kgckbdhe" />
            </motion.div>
            <p className={`text-[20px] font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-3307" data-mp="rjk1x0hqzvwq">
              Verifying...
            </p>
            <p className={`text-[14px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3308" data-mp="nirr42t89iyc">
              Saving Payee details
            </p>
          </motion.div>;
      // ─── Payee SAVED ─────────────────────────────────────────────────────────
      case 'Payee_saved':
        return <motion.div key="Payee_saved" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3309" data-mp="3uc7t1dzvikab">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 z-10 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3310" data-mp="cvzxuin32ryq">
              <div className="w-10" data-id="element-3311" data-mp="hwnqt9uvhivl" />
              <img src="/Dark_theme_logo-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-3312" data-mp="lw6ay3evdpxd" />

              <div className="w-10" data-id="element-3313" data-mp="2nnsu5isisgja" />
            </div>
            <div className="px-6 flex-1 flex flex-col items-center justify-center pb-8" data-id="element-3314" data-mp="2np2s87zdhopx">
              <motion.div initial={{
              scale: 0.5,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              type: 'spring',
              damping: 15,
              stiffness: 200,
              delay: 0.1
            }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6" data-id="element-3315" data-mp="2snxejjwjtves">
                <CheckCircleIcon className="w-14 h-14 text-[#22C55E]" strokeWidth={1.5} data-id="element-3316" data-mp="3404o6a9clm7f" />
              </motion.div>
              <motion.h1 initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.25
            }} className={`text-[28px] font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3317" data-mp="2u2q8wdz5v3gp">
                Payee Added Successfully
              </motion.h1>
              <motion.p initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.35
            }} className={`text-[15px] text-center max-w-[280px] mb-4 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3318" data-mp="37bgzjvsxkmf0">
                <span className={`font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3319" data-mp="3c9sdahmcjh2i">
                  {selectedRecipient?.name}
                </span>{' '}
                has been saved to your Payees.
              </motion.p>
              <motion.div initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.55
            }} className="w-full flex justify-center mt-8" data-id="element-3320" data-mp="20tbrm6k78477">
                {/* ── OK CTA ── */}
                <button onClick={() => {
                setNewPayeeName('');
                setNewPayeeAccount('');
                setNewPayeeRelationship('');
                setNewPayeeBank('');
                setNewPayeeNickname('');
                onNavigateHome?.();
              }} style={ctaStyle} data-id="element-3321" data-mp="278iz2x39rce0">
                  OK
                </button>
              </motion.div>
            </div>
          </motion.div>;
      // ─── REVIEW ──────────────────────────────────────────────────────────────
      case 'review':
        return <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3322" data-mp="qx5bs0668sfu">
            <div className="px-5 pt-6 pb-4 flex items-center justify-between" data-id="element-3323" data-mp="1pct7h8l9aomp">
              <button onClick={() => navigateTo('add_Payee', -1)} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} aria-label="Back" data-id="element-3324" data-mp="1ubw88gu7e8ov">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-3325" data-mp="11o0e9jw0quvs" />
                <span className={`text-[16px] font-regular ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3326" data-mp="16mbnbsehp1cy">
                  Review
                </span>
              </button>
              <button onClick={onNavigateHome} className="p-2 -mr-2" aria-label="Home" data-id="element-3327" data-mp="2i0yphnkxdziu">
                <HomeIcon className="w-6 h-6 text-[#5A1408]" strokeWidth={2} data-id="element-3328" data-mp="bdhvdhdqs7f5" />
              </button>
            </div>
            <div className="px-5 flex-1 flex flex-col pb-6 space-y-4" data-id="element-3329" data-mp="6dv8eg843ct5">
              <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35
            }} className="text-center pl-[20px] ml-[6px]" style={{
              width: '349px',
              height: '150px',
              borderRadius: '8px',
              padding: '0 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundImage: isDark ? 'linear-gradient(#2D1810, #2D1810), linear-gradient(to right, rgba(243,112,36,0.36) 0%, rgba(255,255,255,0.36) 18%, rgba(255,255,255,0.36) 59%, rgba(243,112,36,0.36) 100%)' : 'linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(to right, rgba(243,112,36,0.36) 0%, rgba(255,255,255,0.36) 18%, rgba(255,255,255,0.36) 59%, rgba(243,112,36,0.36) 100%)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              border: '1px solid transparent',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.02)'
            }} data-id="element-3330" data-mp="6gr7w8zly9jp">
                <div className="flex items-baseline justify-center gap-2" data-id="element-3331" data-mp="3le7c2m0719xd">
                  <span className="text-[20px] font-medium" style={{
                  color: isDark ? '#B8A89E' : '#818285'
                }} data-id="element-3332" data-mp="3pf99cxajvbwp">
                    PKR
                  </span>
                  <span className="text-[48px] font-medium leading-none" style={{
                  color: isDark ? '#FFFFFF' : '#5A1408',
                  letterSpacing: '-1px'
                }} data-id="element-3333" data-mp="384voiebkuahk">
                    {amount ? Number(amount.replace(/,/g, '')).toLocaleString('en-US') : '0'}
                  </span>
                </div>
                <p className="text-[12px] mt-2 font-medium" style={{
                color: isDark ? '#B8A89E' : '#1A1A1A'
              }} data-id="element-3334" data-mp="3rrbhtv8r10jm">
                  Transaction fees: 0.00
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              delay: 0.05
            }} className="rounded-xl px-5 py-5 relative" style={{
              background: isDark ? '#2D1810' : '#FFFFFF',
              border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #F0E8E3',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.02)'
            }} data-id="element-3335" data-mp="3vvrkzoqz9tv5">
                <div aria-hidden className="absolute" style={{
                left: 27,
                top: 36,
                bottom: 60,
                width: 0,
                borderLeft: '1px dashed #F37024'
              }} data-id="element-3336" data-mp="2xxvic8q29zw6" />

                <div className="flex gap-4 pb-5" data-id="element-3337" data-mp="1hmhtv8h899wf">
                  <div className="shrink-0 pt-1" data-id="element-3338" data-mp="1cm3x4ml2tmdp">
                    <div className="w-4 h-4 rounded-full bg-[#F37024] border-[3px] border-white shadow-[0_0_0_1px_#F37024]" data-id="element-3339" data-mp="1cnev25rubzww" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3340" data-mp="3t80j4xz42rry">
                    <div className="flex items-center justify-between mb-1" data-id="element-3341" data-mp="3t9lbcgxydymn">
                      <p className="text-[14px] font-bold" style={{
                      color: '#5A1408'
                    }} data-id="element-3342" data-mp="3eikrlfrraib7">
                        Pay From
                      </p>
                      {!isOwnAccountTransfer && <span className="text-[10px] font-bold px-3 py-1 rounded-full bg-[#F5F5F5] text-[#1A1A1A]" data-id="element-3343" data-mp="3aewkey71doqh">
                          {transferMethod === 'raast' ? 'RAAST' : 'IBFT'}
                        </span>}
                    </div>
                    <div className="flex items-center gap-2" data-id="element-3344" data-mp="tt56n1f7xnth">
                      <p className="text-[13px] font-medium" style={{
                      color: isDark ? '#FFFFFF' : '#1A1A1A'
                    }} data-id="element-3345" data-mp="ys8awjndjq0e">
                        {payFromAccount.holder}
                      </p>
                      <span className="text-[13px] text-[#818285]" data-id="element-3346" data-mp="nblkc6losldu">-</span>
                      <p className="text-[12px]" style={{
                      color: isDark ? '#B8A89E' : '#1A1A1A'
                    }} data-id="element-3347" data-mp="jb1kuwdn55oj">
                        {payFromAccount.number.replace(/\D/g, '')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="h-px w-full ml-8 mb-4" style={{
                background: isDark ? 'rgba(243,112,36,0.12)' : '#F0E8E3',
                width: 'calc(100% - 32px)'
              }} data-id="element-3348" data-mp="168v3rnr0hyv3" />

                <div className="flex gap-4" data-id="element-3349" data-mp="1tz8yxn4w8dtw">
                  <div className="shrink-0 pt-1" data-id="element-3350" data-mp="3cntm2kn2ekee">
                    <div className="w-4 h-4 rounded-full bg-white border border-[#F37024]" data-id="element-3351" data-mp="3cp4pgjcllq4z" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3352" data-mp="3hor32twc98eb">
                    <p className="text-[14px] font-bold mb-1" style={{
                    color: '#F37024'
                  }} data-id="element-3353" data-mp="3lnytgus9disk">
                      Pay To
                    </p>
                    <div className="flex items-center gap-2" data-id="element-3354" data-mp="3vkldxigoa7u2">
                      <p className="text-[13px] font-medium" style={{
                      color: isDark ? '#FFFFFF' : '#1A1A1A'
                    }} data-id="element-3355" data-mp="4etpcnijn39d">
                        {selectedRecipient?.name}
                      </p>
                      <span className="text-[13px] text-[#818285]" data-id="element-3356" data-mp="i92v4sixt1d0">-</span>
                      <p className="text-[12px]" style={{
                      color: isDark ? '#B8A89E' : '#1A1A1A'
                    }} data-id="element-3357" data-mp="n7nwiv9xdr31">
                        {selectedRecipient?.accountNumber}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              delay: 0.12
            }} className={`rounded-xl px-5 py-4 ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.18)]' : 'bg-[#F9F9F9]'}`} data-id="element-3358" data-mp="ebnecp6moic5">
                <div className="flex items-center justify-between" data-id="element-3359" data-mp="1bxl0cwk7v5su">
                  <div className="flex-1 min-w-0" data-id="element-3360" data-mp="16z9qiltx8vam">
                    <p className={`text-[14px] font-bold ${isDark ? 'text-white' : 'text-[#5A5B5E]'} ${cameFromSavedPayee ? 'opacity-50' : ''}`} data-id="element-3361" data-mp="16yhx9hnsr9ni">
                      Save as Payee
                    </p>
                    <p className={`text-[12px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'} ${cameFromSavedPayee ? 'opacity-50' : ''}`} data-id="element-3362" data-mp="15i44unznw0iz">
                      For quick transfer in future
                    </p>
                  </div>
                  <button onClick={() => setSavePayee(!savePayee)} disabled={cameFromSavedPayee} className={`relative w-12 h-7 rounded-full transition-colors shrink-0 disabled:opacity-50 ${savePayee ? 'bg-[#F37024]' : isDark ? 'bg-[#3D2518]' : 'bg-[#D4C4BB]'}`} data-id="element-3363" data-mp="1xbmkzc1kxyio">
                    <motion.div className="absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm" animate={{
                    left: savePayee ? 24 : 4
                  }} transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30
                  }} data-id="element-3364" data-mp="2g7w62jawfpc6" />
                  </button>
                </div>
                <AnimatePresence data-id="element-3365" data-mp="2gvc12iqb4wuf">
                  {savePayee && <motion.div initial={{
                  opacity: 0,
                  height: 0,
                  marginTop: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto',
                  marginTop: 14
                }} exit={{
                  opacity: 0,
                  height: 0,
                  marginTop: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-3366" data-mp="2gx6aazs7v0io">
                      <label className={`text-[12px] font-bold block mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-3367" data-mp="1e4u19bodm9mu">
                        Nickname <span className="text-red-500" data-id="element-3368" data-mp="1o1fc4dn2ueyl">*</span>
                      </label>
                      <input type="text" value={newPayeeNickname} onChange={e => {
                    const val = e.target.value.replace(/[^a-zA-Z ]/g, '').slice(0, 40);
                    setNewPayeeNickname(val);
                    setNicknameError('');
                  }} placeholder="e.g. Asad Family" className={`w-full rounded-xl py-3 px-4 text-[14px] outline-none border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#F0E8E3]'} ${nicknameError ? 'border-red-400' : ''}`} data-id="element-3369" data-mp="1syyyy1xuc025" />

                      {nicknameError && <p className="text-red-500 text-[11px] mt-1.5 ml-1" data-id="element-3370" data-mp="25phifqedmo2a">
                          {nicknameError}
                        </p>}
                    </motion.div>}
                </AnimatePresence>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              delay: 0.15
            }} className="rounded-xl px-5 py-4" style={{
              background: isDark ? '#2D1810' : '#FFFFFF',
              border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #F0E8E3',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.02)'
            }} data-id="element-3371" data-mp="2aokiewfi243e">
                <div className="flex items-center justify-between mb-3 relative" data-id="element-3372" data-mp="381inmi4qyr68">
                  <span className="text-[14px] font-medium" style={{
                  color: isDark ? '#FFFFFF' : '#1A1A1A'
                }} data-id="element-3373" data-mp="383lm7qjytsmk">
                    Purpose:
                  </span>
                  <button onClick={() => {
                  if (!isOwnAccountTransfer) setShowPurposeDropdown(!showPurposeDropdown);
                }} className="flex items-center gap-2 text-[13px] font-[600]" style={{
                  color: isDark ? '#FFFFFF' : '#5A5B5E'
                }} data-id="element-3374" data-mp="2kd7s7uhtljpc">
                    {isOwnAccountTransfer ? 'Transfer to own account' : purpose || 'Other'}
                    {!isOwnAccountTransfer && <ChevronDownIcon className="w-4 h-4" data-id="element-3375" data-mp="2l50r3jbg5ihf" />}
                  </button>
                  <AnimatePresence data-id="element-3376" data-mp="2p9i9pf62lpqm">
                    {showPurposeDropdown && <motion.div initial={{
                    opacity: 0,
                    y: -8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -8
                  }} className={`absolute top-full right-0 mt-2 w-48 rounded-xl shadow-lg z-30 max-h-56 overflow-y-auto ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.2)]' : 'bg-white border border-[#F0E8E3]'}`} data-id="element-3377" data-mp="2u6h31vhwtsnr">
                        {PURPOSES.map(p => <button key={p} onClick={() => {
                      setPurpose(p);
                      setShowPurposeDropdown(false);
                    }} className={`w-full text-left px-4 py-3 text-[14px] ${isDark ? 'text-white hover:bg-[#3D2518]' : 'text-[#1A1A1A] hover:bg-[#FFF7F3]'} ${p === purpose ? 'font-bold text-[#F37024]' : ''}`} data-id="element-3378" data-mp="11civ2bupkwqc">
                            {p}
                          </button>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <div className="h-px w-full mb-3" style={{
                background: isDark ? 'rgba(243,112,36,0.12)' : '#F0E8E3'
              }} data-id="element-3379" data-mp="28q2p66pc935k" />

                <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Add your Comments" className="w-full text-[13px] outline-none bg-transparent" style={{
                color: isDark ? '#FFFFFF' : '#1A1A1A'
              }} data-id="element-3380" data-mp="2mvunmln9y582" />
              </motion.div>
              <div className="flex-1 min-h-[20px]" data-id="element-3381" data-mp="292mtonbq0xn7" />
              {/* ── Pay CTA ── */}
              <div className="flex justify-center" data-id="element-3382" data-mp="1v2lt78zdbs89">
                <button onClick={() => {
                if (savePayee) {
                  if (!newPayeeNickname.trim()) {
                    setNicknameError('Nickname is required');
                    return;
                  }
                  const saved: Recipient = {
                    id: `${Date.now()}`,
                    name: newPayeeNickname.trim(),
                    bank: selectedRecipient?.bank || newPayeeBank,
                    accountNumber: selectedRecipient?.accountNumber || newPayeeAccount,
                    icon: BuildingIcon,
                    color: '#F37024'
                  };
                  setPayees(prev => [...prev, saved]);
                  setSelectedRecipient(saved);
                }
                navigateTo('mpin');
              }} style={ctaStyle} data-id="element-3383" data-mp="1v3wy5c4jv60y">
                  Pay{' '}
                  <ArrowRightIcon className="w-5 h-5 ml-2" strokeWidth={2.5} data-id="element-3384" data-mp="2ahzddj6bbwd6" />
                </button>
              </div>
            </div>
          </motion.div>;
      // ─── MPIN ────────────────────────────────────────────────────────────────
      case 'mpin':
        return <motion.div key="mpin" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3385" data-mp="b77y29v1qbr5">
            <div className={`px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 z-10 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3386" data-mp="mmt0phumczek">
              <button onClick={() => navigateTo('review', -1)} className={`flex items-center gap-1 -ml-2 px-2 py-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3387" data-mp="3q2zv0xr59kao">
                <ArrowLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-3388" data-mp="3tyy7ne6mzr8" />
              </button>
              <img src="/Dark_theme_logo-removebg-preview.png" alt="digiBOP Logo" className="h-14 w-auto" data-id="element-3389" data-mp="8m04msoo5w9c" />

              <div className="w-10" data-id="element-3390" data-mp="3catom3dsmrof" />
            </div>
            <div className="px-6 mt-4 flex-1 flex flex-col pb-8" data-id="element-3391" data-mp="3c98ycpyihuwd">
              <div className="text-center mb-10" data-id="element-3392" data-mp="34rkh6foul320">
                <h1 className={`text-[28px] font-bold tracking-tight mb-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3393" data-mp="34p81682nh2rl">
                  Verify Payment
                </h1>
                <p className={`text-[15px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3394" data-mp="2uuy081zn6w59">
                  Enter your 6-digit MPIN to confirm
                </p>
              </div>
              <div className={`rounded-3xl p-6 mb-10 shadow-sm ${isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.1)]' : 'bg-white'}`} data-id="element-3395" data-mp="2h1zwnsd1pxqm">
                <div className="flex justify-between items-center mb-4" data-id="element-3396" data-mp="2c0ak28a6dc0f">
                  <span className={`text-[13px] font-medium ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3397" data-mp="2753jh9c7cnfq">
                    Amount to send
                  </span>
                  <span className={`text-[20px] font-bold ${isDark ? 'text-white' : 'text-[#661806]'}`} data-id="element-3398" data-mp="1y3njzwmxe5qa">
                    Rs {parseFloat(amount || '0').toLocaleString()}
                  </span>
                </div>
                <div className={`h-px w-full mb-4 ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-3399" data-mp="1ehu2cenrukhf" />

                <div className="flex items-center gap-3" data-id="element-3400" data-mp="1jgf7mshvrucq">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F3]'}`} data-id="element-3401" data-mp="275hkzo5hf11f">
                    {selectedRecipient?.icon && <selectedRecipient.icon className="w-5 h-5 text-[#F37024]" strokeWidth={1.5} data-id="element-3402" data-mp="1xfobcsowgvzt" />}
                  </div>
                  <div data-id="element-3403" data-mp="2q2ivtplpvvda">
                    <p className={`text-[14px] font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3404" data-mp="2uv27gh0uggfg">
                      {selectedRecipient?.name}
                    </p>
                    <p className={`text-[12px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3405" data-mp="309bxj782s6zf">
                      {selectedRecipient?.bank}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center gap-3 mb-12" data-id="element-3406" data-mp="2lb02f50cp6ad">
                {[...Array(6)].map((_, i) => <div key={i} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${mpin[i] ? isDark ? 'bg-[#3D2518] text-white border-2 border-[#F37024]' : 'bg-white text-[#303030] border-2 border-[#F37024] shadow-sm' : isDark ? 'bg-[#2D1810] border-2 border-[#3D2518]' : 'bg-[#F5EBE6] border-2 border-transparent'}`} data-id="element-3407" data-mp="2ha6bs3vixdds" data-mpi={i}>
                    {mpin[i] ? '•' : ''}
                  </div>)}
              </div>
              <div className="mt-auto grid grid-cols-3 gap-y-6 gap-x-4 max-w-[300px] mx-auto w-full" data-id="element-3408" data-mp="201nez834ck6y">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} onClick={() => {
                if (mpin.length < 6) setMpin(prev => prev + num);
              }} className={`h-14 rounded-2xl text-[24px] font-medium flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-white hover:bg-[#2D1810]' : 'text-[#303030]'}`} data-id="element-3409" data-mp="290iha931la4z">
                    {num}
                  </button>)}
                <button onClick={() => setBiometricPhase('scanning')} className={`h-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-[#F37024] hover:bg-[#2D1810]' : 'text-[#F37024]'}`} data-id="element-3410" data-mp="2wqcuirr9x0pr">
                  <FingerprintIcon className="w-8 h-8" strokeWidth={1.5} data-id="element-3411" data-mp="ut2lwcjb2d03" />
                </button>
                <button onClick={() => {
                if (mpin.length < 6) setMpin(prev => prev + '0');
              }} className={`h-14 rounded-2xl text-[24px] font-medium flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-white hover:bg-[#2D1810]' : 'text-[#303030]'}`} data-id="element-3412" data-mp="q2chb1ipee6g">
                  0
                </button>
                <button onClick={() => setMpin(prev => prev.slice(0, -1))} className={`h-14 rounded-2xl flex items-center justify-center transition-colors active:scale-95 ${isDark ? 'text-[#B8A89E] hover:bg-[#2D1810]' : 'text-[#818285]'}`} data-id="element-3413" data-mp="18sc2vefc5b54">
                  <ArrowLeftIcon className="w-7 h-7" strokeWidth={2} data-id="element-3414" data-mp="3ojq3l4ipo09s" />
                </button>
              </div>
              <AnimatePresence data-id="element-3415" data-mp="3ki4xc20omk8k">
                {biometricPhase !== 'entering_mpin' && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="fixed inset-0 z-50 flex flex-col items-center justify-center" style={{
                background: isDark ? 'linear-gradient(180deg, rgba(26,14,8,0.95) 0%, rgba(45,24,16,0.95) 100%)' : 'linear-gradient(180deg, rgba(255,247,243,0.95) 0%, rgba(253,238,229,0.95) 100%)',
                backdropFilter: 'blur(10px)'
              }} data-id="element-3416" data-mp="2ut1btjrhg7dk">
                    <AnimatePresence mode="wait" data-id="element-3417" data-mp="3se73ejn7n35k">
                      {biometricPhase === 'scanning' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.9
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.9
                  }} className="flex flex-col items-center gap-6" data-id="element-3418" data-mp="1t5idpt96p9nq">
                          <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
                      background: 'rgba(243, 112, 36, 0.12)',
                      border: '2px solid rgba(243, 112, 36, 0.3)'
                    }} data-id="element-3419" data-mp="29h3kyteb0xaa">
                            {scanType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-3420" data-mp="3bd87ptrsm8pj" /> : <ScanFaceIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-3421" data-mp="32aqjxawhp80f" />}
                          </motion.div>
                          <div className="text-center" data-id="element-3422" data-mp="2empi0kttgptz">
                            <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3423" data-mp="2eoaam4wklyp7">
                              {scanType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
                            </p>
                            <p className={`text-[13px] ${isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]'}`} data-id="element-3424" data-mp="1q2m3mntahvxe">
                              {scanType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
                            </p>
                          </div>
                          <button onClick={() => setBiometricPhase('entering_mpin')} className={`mt-4 text-[14px] font-bold underline ${isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-3425" data-mp="28zdlqtx3i4zv">
                            Use MPIN Instead
                          </button>
                        </motion.div>}
                      {biometricPhase === 'verified' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.9
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.9
                  }} className="flex flex-col items-center gap-6" data-id="element-3426" data-mp="2npl4lar0lq13">
                          <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
                      border: '2px solid rgba(34, 197, 94, 0.3)'
                    }} data-id="element-3427" data-mp="25wgxdmj9k5g">
                            <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-3428" data-mp="3cyrzyo41dkrd" />
                          </motion.div>
                          <div className="text-center" data-id="element-3429" data-mp="3375r2yf67xrs">
                            <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3430" data-mp="2y9ng72hlf8h5">
                              Verified Successfully
                            </p>
                            <p className={`text-[13px] ${isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]'}`} data-id="element-3431" data-mp="2p7e96mlj9x8i">
                              Processing payment...
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>;
      // ─── PROCESSING ──────────────────────────────────────────────────────────
      case 'processing':
        return <motion.div key="processing" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className={`w-full min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3432" data-mp="1y17c6lcr74c4">
            <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-20 h-20 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] mb-6" data-id="element-3433" data-mp="2vmlg9c0oz3es" />

            <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3434" data-mp="1aco0dvuhw9fa">
              Processing Payment
            </h2>
            <p className={`text-[15px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3435" data-mp="3jksssd6t09ww">
              Please wait while we process your transaction
            </p>
          </motion.div>;
      // ─── SUCCESS ─────────────────────────────────────────────────────────────
      case 'success':
        const now = new Date();
        const formattedDate = now.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }).replace(/ /g, '-');
        const formattedTime = now.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
        const txnId = '78343846823';
        const amountNum = Number(amount.replace(/,/g, '')) || 0;
        const payFromDigits = payFromAccount.number.replace(/[^\d]/g, '');
        return <motion.div key="success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="w-full min-h-screen flex flex-col relative overflow-hidden" style={{
          background: '#F2F2F2'
        }} data-id="element-3436" data-mp="36znn75abd2n4">
            {!isDark && <div aria-hidden className="absolute pointer-events-none" style={{
            top: '-5%',
            left: '-15%',
            width: '130%',
            height: '52%',
            background: 'radial-gradient(ellipse 60% 55% at 52% 42%, rgba(243,130,50,0.38) 0%, rgba(243,150,80,0.20) 38%, rgba(243,112,36,0.06) 65%, transparent 80%)',
            filter: 'blur(22px)',
            zIndex: 0
          }} data-id="element-3437" data-mp="3pw5ooeeysgwk" />}
            {isDark && <div aria-hidden className="absolute pointer-events-none" style={{
            top: '-5%',
            left: '-15%',
            width: '130%',
            height: '52%',
            background: 'radial-gradient(ellipse 60% 55% at 52% 42%, rgba(243,112,36,0.18) 0%, transparent 70%)',
            filter: 'blur(22px)',
            zIndex: 0
          }} data-id="element-3438" data-mp="rlvcyvbvkewl" />}
            <div className="px-5 pt-6 pb-4 flex items-center justify-between relative z-10" data-id="element-3439" data-mp="15d0czyj5n97g">
              <h1 className={`text-[18px] font-regular ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-3440" data-mp="10k7a7g78yay0">
                Receipt
              </h1>
              <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-3441" data-mp="1t335gfqzeh6l">
                <HomeIcon className="w-6 h-6" strokeWidth={1.8} data-id="element-3442" data-mp="2q19mw9leqljm" />
              </button>
            </div>
            <div className="px-5 flex-1 flex flex-col pb-6 space-y-4 relative z-10" data-id="element-3443" data-mp="2l26pnbbo5jm7">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.4
            }} className="flex flex-col items-center relative overflow-hidden mx-auto" style={{
              width: '357px',
              height: '257px',
              borderRadius: '15px',
              background: isDark ? 'rgba(45,24,16,0.82)' : 'rgba(255,255,255,0.72)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1.5px solid #F37024',
              boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)' : '0 8px 32px rgba(243,112,36,0.10), inset 0 1px 0 rgba(255,255,255,0.9)',
              padding: '28px 24px',
              justifyContent: 'center'
            }} data-id="element-3444" data-mp="2zyo0m1fy4w88">
                <div className="w-14 h-14 rounded-full bg-[#22C55E] flex items-center justify-center mb-3 z-10" style={{
                boxShadow: '0 4px 12px rgba(34,197,94,0.35)'
              }} data-id="element-3445" data-mp="674bf6asr2rq">
                  <CheckIcon className="w-7 h-7 text-white" strokeWidth={3} data-id="element-3446" data-mp="3emo340vtkd4o" />
                </div>
                <p className="text-[15px] font-medium mb-5 z-10" style={{
                color: isDark ? '#C4B5AA' : '#5A5B5E'
              }} data-id="element-3447" data-mp="3in7zh28kjcpr">
                  Success
                </p>
                <div className="flex items-baseline justify-center gap-2 mb-5 z-10" data-id="element-3448" data-mp="1f5jlyqlfm9x1">
                  <span className="text-[22px] font-medium" style={{
                  color: isDark ? '#B8A89E' : '#9E9E9E'
                }} data-id="element-3449" data-mp="1af37r49sek9m">
                    PKR
                  </span>
                  <span className="font-bold leading-none" style={{
                  fontSize: '52px',
                  color: isDark ? '#FFFFFF' : '#5A1408',
                  letterSpacing: '-1.5px'
                }} data-id="element-3450" data-mp="11fy9lz250sxz">
                    {amountNum.toLocaleString('en-US')}
                  </span>
                </div>
                <p className="text-[13px] font-medium z-10 mb-1" style={{
                color: isDark ? '#C4B5AA' : '#303030'
              }} data-id="element-3451" data-mp="1k4d1spo6yd0d">
                  {formattedDate}, {formattedTime}
                </p>
                <p className="text-[13px] font-medium z-10" style={{
                color: isDark ? '#C4B5AA' : '#303030'
              }} data-id="element-3452" data-mp="3us87k6qhet6f">
                  Ref# {txnId}
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.1
            }} className="rounded-2xl px-5 py-5" style={{
              background: isDark ? '#2D1810' : '#FFFFFF',
              border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #E8E8E8',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.04)'
            }} data-id="element-3453" data-mp="daxa67ax5bzm">
                <div className="flex items-start justify-between mb-5" data-id="element-3454" data-mp="ndsus0xjppy6">
                  <span className="text-[14px] font-bold mt-0.5" style={{
                  color: '#5A1408'
                }} data-id="element-3455" data-mp="nfna8zq1spuv">
                    To:
                  </span>
                  <div className="text-right" data-id="element-3456" data-mp="8nv5fhnvqu7e">
                    <p className="text-[14px] font-medium" style={{
                    color: isDark ? '#FFFFFF' : '#1A1A1A'
                  }} data-id="element-3457" data-mp="8mk0vfmm1if8">
                      {selectedRecipient?.name || 'Recipient'}
                    </p>
                    <p className="text-[13px] mt-0.5" style={{
                    color: isDark ? '#B8A89E' : '#5A5B5E'
                  }} data-id="element-3458" data-mp="dlwjfo6g83c0">
                      *********
                      {selectedRecipient?.accountNumber?.slice(-4) || '1234'}
                    </p>
                  </div>
                </div>
                <div className="h-px w-full mb-5" style={{
                background: isDark ? 'rgba(243,112,36,0.1)' : '#F0F0F0'
              }} data-id="element-3459" data-mp="21eyr148ibv8r" />

                <div className="flex items-start justify-between" data-id="element-3460" data-mp="2gaenth0h258u">
                  <span className="text-[14px] font-bold mt-0.5" style={{
                  color: '#5A1408'
                }} data-id="element-3461" data-mp="2gb68hwsj6ikc">
                    From:
                  </span>
                  <div className="text-right" data-id="element-3462" data-mp="2basbd9sgwt13">
                    <p className="text-[14px] font-medium" style={{
                    color: isDark ? '#FFFFFF' : '#1A1A1A'
                  }} data-id="element-3463" data-mp="2b9gwoexhi0v7">
                      {payFromAccount.holder}
                    </p>
                    <p className="text-[13px] mt-0.5" style={{
                    color: isDark ? '#B8A89E' : '#5A5B5E'
                  }} data-id="element-3464" data-mp="ftimrqbswjg7">
                      *********{payFromDigits.slice(-4)}
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.15
            }} className="rounded-2xl px-5 py-4" style={{
              background: isDark ? '#2D1810' : '#FFFFFF',
              border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #E8E8E8',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.04)'
            }} data-id="element-3465" data-mp="3t96sm4a9ecg7">
                <button onClick={() => setShowAdditionalInfo(!showAdditionalInfo)} className="w-full flex items-center justify-between mb-1" data-id="element-3466" data-mp="1urczddce70zk">
                  <span className="text-[14px] font-medium" style={{
                  color: isDark ? '#FFFFFF' : '#1A1A1A'
                }} data-id="element-3467" data-mp="122pcxefwq5kf">
                    Additional Information
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 transition-transform ${showAdditionalInfo ? 'rotate-180' : ''}`} style={{
                  color: isDark ? '#B8A89E' : '#1A1A1A'
                }} data-id="element-3468" data-mp="1c2znssegz0wf" />
                </button>
                <AnimatePresence initial={false} data-id="element-3469" data-mp="2rj8bexbi9lo4">
                  {showAdditionalInfo && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-3470" data-mp="3dgkzh1iwrk5r">
                      <div className="pt-4 space-y-4" data-id="element-3471" data-mp="a04dltlowk1u">
                        {[{
                      label: 'Channel',
                      value: transferMethod === 'raast' ? 'RAAST' : '1LINK'
                    }, {
                      label: 'Transaction Fee',
                      value: 'PKR 0'
                    }, {
                      label: 'Destination Bank',
                      value: selectedRecipient?.bank || newPayeeBank || 'Meezan Bank'
                    }, {
                      label: 'Purpose of Transfer',
                      value: purpose || 'Other'
                    }, {
                      label: 'Comments',
                      value: note || 'Advance Payment ...'
                    }].map(item => <div key={item.label} className="flex justify-between items-center" data-id="element-3472" data-mp="1o1ac73k4j1fl">
                            <span className="text-[13px] font-bold" style={{
                        color: '#F37024'
                      }} data-id="element-3473" data-mp="2wl1ifu1a4hu3">
                              {item.label}
                            </span>
                            <span className="text-[13px] font-medium" style={{
                        color: isDark ? '#FFFFFF' : '#1A1A1A'
                      }} data-id="element-3474" data-mp="2rl6sptt8pyam">
                              {item.value}
                            </span>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>

              <div className="rounded-xl px-5 py-4 flex items-center gap-3" style={{
              background: isDark ? '#2D1810' : '#FFFFFF',
              border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #E8E8E8',
              boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.04)'
            }} data-id="element-3475" data-mp="1uuecmw6b468u">
                <div className="w-10 h-10 rounded-full bg-[#F37024]/10 flex items-center justify-center shrink-0" data-id="element-3476" data-mp="1wde9hgm1derd">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-3477" data-mp="18l7kj2in4a1z">
                    <path d="M18 8A6 6 0 0 0 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="#F37024" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3478" data-mp="1dmn1aidsbuek" />
                    <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="#F37024" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3479" data-mp="2l078m2k0njjn" />
                  </svg>
                </div>
                <p className="text-[12px]" style={{
                color: isDark ? '#B8A89E' : '#5A5B5E'
              }} data-id="element-3480" data-mp="223fv69r5nyni">
                  Notification sent to your registered Mobile/Email
                </p>
              </div>

              <div className="flex-1 min-h-[12px]" data-id="element-3481" data-mp="2fyrpwh5b9p4f" />
              <div className="flex justify-center mb-2" data-id="element-3482" data-mp="3mfwoz2kev2a8">
                <img src="/Dark_theme_logo-removebg-preview.png" alt="digiBOP" className="h-10 object-contain" style={{
                filter: isDark ? 'brightness(0) invert(1)' : 'none'
              }} data-id="element-3483" data-mp="3mjav8lw73vmy" />
              </div>
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.2
            }} className="flex items-start justify-around pt-1 pb-2" data-id="element-3484" data-mp="3rtmxwe9tg8h8">
                <button className="flex flex-col items-center gap-1.5 px-4 active:scale-95 transition-transform" data-id="element-3485" data-mp="38w291771x9wm">
                  <ShareIcon style={{
                  width: 26,
                  height: 26,
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} strokeWidth={1.8} data-id="element-3486" data-mp="34v9zsdy6j50c" />

                  <span className="text-[12px] font-medium" style={{
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} data-id="element-3487" data-mp="1a595m1141hqi">
                    Share
                  </span>
                </button>
                <button className="flex flex-col items-center gap-1.5 px-4 active:scale-95 transition-transform" data-id="element-3488" data-mp="155wh7bulc2lt">
                  <ImageIcon style={{
                  width: 26,
                  height: 26,
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} strokeWidth={1.8} data-id="element-3489" data-mp="rdzu5m7s4iwa" />

                  <span className="text-[12px] font-medium" style={{
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} data-id="element-3490" data-mp="mfy24bsdl98o">
                    Save Image
                  </span>
                </button>
                <button onClick={onNavigateHome} className="flex flex-col items-center gap-1.5 px-4 active:scale-95 transition-transform" data-id="element-3491" data-mp="3pyugfqrxveri">
                  <ArrowRightIcon style={{
                  width: 26,
                  height: 26,
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} strokeWidth={1.8} data-id="element-3492" data-mp="3l241j6rvep7o" />

                  <span className="text-[12px] font-medium" style={{
                  color: isDark ? '#F5CBA7' : '#5A1408'
                }} data-id="element-3493" data-mp="cqhmceyx785n">
                    Logout
                  </span>
                </button>
              </motion.div>
            </div>
          </motion.div>;
    }
  };
  return <div className={`w-full min-h-screen relative overflow-hidden ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3494" data-mp="vjlj497fq14h">
      <AnimatePresence mode="wait" custom={direction} data-id="element-3495" data-mp="qljz9m26pxry">
        {renderStep()}
      </AnimatePresence>
      <NotificationPanel isOpen={showNotifications} onClose={() => setShowNotifications(false)} data-id="element-3496" data-mp="3k9m278qbh4kw" />
    </div>;
}